import React, { useState } from "react";
import { useUserAuth } from "../../../context/userAuthContext";
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import {
  Shield,
  Mail,
  CheckCircle,
  AlertCircle,
  Loader2,
  UserPlus,
} from "lucide-react";

const TempMakeAdmin = () => {
  const { user } = useUserAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);

  const searchUserByEmail = async () => {
    if (!email.trim()) {
      setMessage({ text: "Por favor ingresa un correo electrónico", type: "error" });
      return;
    }

    setSearching(true);
    setMessage({ text: "", type: "" });
    setFoundUser(null);

    try {
      // Buscar directamente en Firestore Users collection
      const usersRef = collection(db, "Users");
      const q = query(usersRef, where("email", "==", email.trim().toLowerCase()));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // Si no se encuentra por email, buscar en todos los usuarios (puede que el email esté en otro campo)
        const allUsersSnapshot = await getDocs(usersRef);
        let found = false;

        for (const docSnap of allUsersSnapshot.docs) {
          const userData = docSnap.data();
          // Buscar en diferentes campos posibles
          if (
            (userData.email && userData.email.toLowerCase() === email.trim().toLowerCase()) ||
            (userData.userEmail && userData.userEmail.toLowerCase() === email.trim().toLowerCase())
          ) {
            setFoundUser({
              uid: docSnap.id,
              email: userData.email || userData.userEmail || email.trim().toLowerCase(),
              displayName: userData.displayName || userData.name || "Sin nombre",
              currentRole: userData.type || "usuario",
              exists: true,
            });
            setMessage({
              text: `Usuario encontrado: ${userData.displayName || userData.name || email}`,
              type: "success",
            });
            found = true;
            break;
          }
        }

        if (!found) {
          setMessage({
            text: "Usuario no encontrado. Asegúrate de que el usuario exista en el sistema.",
            type: "warning",
          });
          setFoundUser({ email: email.trim().toLowerCase(), exists: false });
        }
      } else {
        const userDoc = snapshot.docs[0];
        const userData = userDoc.data();
        setFoundUser({
          uid: userDoc.id,
          email: userData.email || email.trim().toLowerCase(),
          displayName: userData.displayName || userData.name || "Sin nombre",
          currentRole: userData.type || "usuario",
          exists: true,
        });
        setMessage({
          text: `Usuario encontrado: ${userData.displayName || userData.name || email}`,
          type: "success",
        });
      }
    } catch (error) {
      console.error("Error searching user:", error);
      setMessage({ text: "Error al buscar usuario: " + error.message, type: "error" });
    } finally {
      setSearching(false);
    }
  };

  const makeAdmin = async () => {
    if (!email.trim()) {
      setMessage({ text: "Por favor ingresa un correo electrónico", type: "error" });
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const emailLower = email.trim().toLowerCase();
      let userId = null;
      let userDocRef = null;

      // Buscar usuario directamente en Firestore (método más confiable para componente temporal)
      const usersRef = collection(db, "Users");
      
      // Primero buscar por email exacto
      const q = query(usersRef, where("email", "==", emailLower));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        // Usuario existe en Users
        userId = snapshot.docs[0].id;
        userDocRef = doc(db, "Users", userId);
      } else {
        // Buscar en todos los usuarios (puede que el email esté en otro campo)
        const allUsersSnapshot = await getDocs(usersRef);
        
        for (const docSnap of allUsersSnapshot.docs) {
          const userData = docSnap.data();
          if (
            (userData.email && userData.email.toLowerCase() === emailLower) ||
            (userData.userEmail && userData.userEmail.toLowerCase() === emailLower)
          ) {
            userId = docSnap.id;
            userDocRef = doc(db, "Users", userId);
            break;
          }
        }
      }

      if (!userId || !userDocRef) {
        setMessage({
          text: "Usuario no encontrado. Por favor verifica el correo o asegúrate de que el usuario exista en el sistema.",
          type: "error",
        });
        setLoading(false);
        return;
      }

      // Actualizar directamente en Firestore (método directo para componente temporal)
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        setMessage({ text: "Usuario no encontrado en la base de datos", type: "error" });
        setLoading(false);
        return;
      }

      const currentData = userDoc.data();
      const oldRole = currentData.type || "usuario";

      // Actualizar el rol a admin
      await updateDoc(userDocRef, {
        type: "admin",
        updatedAt: new Date(),
        roleHistory: [
          ...(currentData.roleHistory || []),
          {
            timestamp: new Date(),
            changedBy: user?.uid || "system",
            changedByEmail: user?.email || "system",
            oldRole: oldRole,
            newRole: "admin",
            reason: "Asignación temporal de administrador",
          },
        ],
      });

      setMessage({
        text: `✅ Usuario ${emailLower} ahora es administrador! (Rol anterior: ${oldRole})`,
        type: "success",
      });
      setFoundUser(null);
      setEmail("");
      
      // Recargar la búsqueda si había un usuario encontrado
      if (foundUser) {
        setTimeout(() => {
          searchUserByEmail();
        }, 1000);
      }
    } catch (error) {
      console.error("Error making admin:", error);
      setMessage({ 
        text: "Error al hacer admin: " + (error.message || "Error desconocido"), 
        type: "error" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 border border-slate-200">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Hacer Administrador
          </h1>
          <p className="text-sm text-slate-600">
            Componente temporal para asignar rol de administrador
          </p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-xl border-2 flex items-center gap-3 ${
              message.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : message.type === "warning"
                  ? "bg-yellow-50 border-yellow-200 text-yellow-800"
                  : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="font-medium text-sm">{message.text}</p>
          </div>
        )}

        {/* Found User Info */}
        {foundUser && foundUser.exists && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-900">Usuario Encontrado</h3>
            </div>
            <div className="text-sm text-blue-800 space-y-1">
              <p>
                <strong>Email:</strong> {foundUser.email}
              </p>
              <p>
                <strong>Nombre:</strong> {foundUser.displayName}
              </p>
              <p>
                <strong>Rol Actual:</strong>{" "}
                <span className="font-semibold">{foundUser.currentRole || "usuario"}</span>
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Correo Electrónico <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && searchUserByEmail()}
                placeholder="usuario@ejemplo.com"
                className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={searchUserByEmail}
              disabled={searching || !email.trim()}
              className="flex-1 px-4 py-3 border-2 border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Buscando...
                </>
              ) : (
                <>
                  Buscar Usuario
                </>
              )}
            </button>
            <button
              onClick={makeAdmin}
              disabled={loading || !email.trim()}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-xl font-semibold text-white hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  Hacer Admin
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <p className="text-xs text-yellow-800 text-center">
            ⚠️ Este es un componente temporal. Úsalo solo para configuración inicial.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TempMakeAdmin;

